import { useEffect, useRef, useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, num, rupee, pnlClass, istDateTime, STRATEGY_LABELS } from "@/lib/api";

const pf = (v) => (v === null || v === undefined ? "—" : v >= 999 ? "∞" : num(v));
const COLORS = ["#94A3B8", "#818CF8", "#F59E0B", "#EC4899", "#10B981", "#38BDF8", "#F97316", "#A3E635", "#14B8A6", "#E879F9", "#FACC15", "#FB7185"];
const axis = { fill: "#64748B", fontSize: 10, fontFamily: "IBM Plex Mono" };

const useResearch = (kind, onDone) => {
  const [res, setRes] = useState(null);
  const [job, setJob] = useState(null);
  const [other, setOther] = useState(false);
  const following = useRef(null);
  useEffect(() => {
    const id = setInterval(() => api.get("/research/active").then((r) => setOther(!!r.data?.id && r.data.kind !== kind)).catch(() => {}), 3000);
    return () => clearInterval(id);
  }, [kind]);
  const follow = async (j) => {
    if (following.current === j.id) return null;
    following.current = j.id;
    let misses = 0;
    setJob(j);
    while (j.status === "running") {
      await new Promise((ok) => setTimeout(ok, 2000));
      try { j = (await api.get(`/research/jobs/${j.id}`)).data; misses = 0; setJob(j); } catch (e) { if (++misses >= 4) throw e; }
    }
    if (j.status === "error") throw new Error(j.error);
    setRes(j.result);
    return j.result;
  };
  const guard = (p) => p.catch((e) => toast.error(errMsg(e))).finally(() => setJob(null));
  useEffect(() => {
    api.get(`/research/latest/${kind}`).then((r) => r.data?.created_at && setRes(r.data));
    api.get("/research/active").then((r) => r.data?.kind === kind && guard(follow(r.data).then(done)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const done = (r) => r && onDone(r);
  const run = (path, body) => guard(api.post(path, body).then((r) => follow(r.data)).then(done));
  return { res, job, run, busy: !!job || other };
};

const Progress = ({ job, id }) => job && (
  <div className="w-full" data-testid={id}>
    <div className="flex justify-between text-[11px] text-slate-400"><span>{job.stage}</span><span className="num">{job.done}/{job.total || "…"}</span></div>
    <div className="mt-1 h-1.5 overflow-hidden rounded bg-slate-800"><div className="h-full bg-indigo-500 transition-[width] duration-500" style={{ width: `${(100 * job.done) / (job.total || 1)}%` }} /></div>
  </div>
);

const mergeEquity = (variants) => {
  const map = {};
  variants.forEach((v, k) => v.equity.forEach((p) => { (map[p.date] ||= { date: p.date })[`v${k}`] = p.equity; }));
  const rows = Object.values(map).sort((a, b) => (a.date < b.date ? -1 : 1));
  const last = {};
  return rows.map((r) => { variants.forEach((_, k) => { if (r[`v${k}`] === undefined) r[`v${k}`] = last[k]; else last[k] = r[`v${k}`]; }); return r; });
};

const PricingToggle = ({ value, onChange, allowRecorded = true }) => {
  const [rd, setRd] = useState(null);
  useEffect(() => { api.get("/research/recorded-readiness").then((r) => setRd(r.data)).catch(() => {}); }, []);
  return (
    <div className="flex items-center gap-2 text-[11px]" data-testid="pricing-toggle">
      {["estimated", "recorded"].map((m) => (
        <button key={m} data-testid={`pricing-${m}`} disabled={m === "recorded" && (!rd?.ready || !allowRecorded)} onClick={() => onChange(m)}
          className={`rounded px-2 py-1 font-semibold uppercase tracking-wider transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${value === m ? "bg-slate-700 text-slate-50" : "text-slate-500 hover:text-slate-300"}`}>{m}</button>
      ))}
      <span className="num text-slate-500" data-testid="recorded-readiness">recorded {rd?.days ?? 0}/{rd?.needed ?? 15} days{rd?.ready ? " · ready" : ""}</span>
    </div>
  );
};

export const VariantLab = ({ disabled }) => {
  const [pricing, setPricing] = useState("estimated");
  const { res, job, run, busy } = useResearch("variants", (r) => {
    const best = [...r.variants].sort((a, b) => (b.out_of_sample.profit_factor || 0) - (a.out_of_sample.profit_factor || 0))[0];
    toast.success(`Variant Lab done: best OOS PF ${pf(best.out_of_sample.profit_factor)} (${best.name})`);
  });
  const go = () => run("/research/variants", { years: 3, oos_pct: 30, pricing });
  return (
    <div className="panel" data-testid="panel-variant-lab">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-800 p-4">
        <span className="eyebrow">Strategy variant lab · 3 years · 30% OOS</span>
        <PricingToggle value={pricing} onChange={setPricing} />
        <Button data-testid="button-run-variants" disabled={disabled || busy} onClick={go} className="ml-auto h-8 bg-indigo-500 text-xs text-white hover:bg-indigo-400">{job ? "Running…" : "Run 8 variants"}</Button>
        <Progress job={job} id="variants-progress" />
      </div>
      {res && <LabResults res={res} rows={res.variants} id="variants" />}
    </div>
  );
};

const LabResults = ({ res, rows, id }) => (
  <>
          {rows.every((v) => v.all.trades === 0) && (
            <p data-testid={`${id}-no-trades`} className="px-4 pt-3 text-xs text-amber-400">No trades in this run{res.pricing === "recorded" ? ": no signals matched a recorded option snapshot yet." : "."}</p>
          )}
          <div className="num px-4 pt-2 text-[11px] text-slate-500">{res.from_date} → {res.to_date} · {res.pricing || "estimated"} pricing · run {istDateTime(res.created_at)}</div>
          <div className="overflow-x-auto">
            <table className="dense num w-full" data-testid={`table-${id}`}>
              <thead><tr><th>Rules</th><th>IS trades</th><th>IS PF</th><th>OOS trades</th><th>OOS win %</th><th>OOS PF</th><th>OOS net</th><th>OOS max DD</th></tr></thead>
              <tbody>
                {rows.map((v, k) => (
                  <tr key={v.name} data-testid={`${id}-row-${k}`}>
                    <td><span className="mr-2 inline-block h-2 w-2 rounded-full" style={{ background: COLORS[k] }} />{v.name}</td>
                    <td>{v.in_sample.trades}</td><td>{pf(v.in_sample.profit_factor)}</td><td>{v.out_of_sample.trades}</td>
                    <td>{num(v.out_of_sample.win_rate)}</td>
                    <td className={v.out_of_sample.profit_factor >= 1.5 ? "up font-semibold" : v.out_of_sample.profit_factor < 1 ? "down" : ""}>{pf(v.out_of_sample.profit_factor)}</td>
                    <td className={pnlClass(v.out_of_sample.net_pnl)}>{rupee(v.out_of_sample.net_pnl)}</td><td>{rupee(v.out_of_sample.max_drawdown)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="h-[260px] p-3" data-testid={`chart-${id}-equity`}>
            <ResponsiveContainer>
              <LineChart data={mergeEquity(rows)}>
                <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={axis} minTickGap={50} padding={{ right: 24 }} />
                <YAxis domain={["auto", "auto"]} tick={axis} width={70} />
                <Tooltip contentStyle={{ background: "#121826", border: "1px solid #334155", fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} itemSorter={null} payload={rows.map((v, k) => ({ value: v.name, type: "line", color: COLORS[k], id: `v${k}` }))} />
                {rows.map((v, k) => <Line key={v.name} dataKey={`v${k}`} name={v.name} stroke={COLORS[k]} dot={false} strokeWidth={k === 0 ? 2 : 1.2} isAnimationActive={false} />)}
              </LineChart>
            </ResponsiveContainer>
          </div>
  </>
);

export const WalkForward = ({ disabled }) => {
  const { res, job, run, busy } = useResearch("walk_forward", (r) =>
    toast.success(`Walk-forward done: ${r.combined.trades} OOS trades, PF ${pf(r.combined.profit_factor)}`));
  const go = () => run("/research/walk-forward", { years: 3, train_months: 12, test_months: 6 });
  const c = res?.combined;
  return (
    <div className="panel" data-testid="panel-walk-forward">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-800 p-4">
        <span className="eyebrow">Walk-forward · re-pick rules every 6 months on the prior 12 months</span>
        <Button data-testid="button-run-walk-forward" disabled={disabled || busy} onClick={go} className="ml-auto h-8 bg-indigo-500 text-xs text-white hover:bg-indigo-400">{job ? "Running…" : "Run walk-forward"}</Button>
        <Progress job={job} id="walk-forward-progress" />
      </div>
      {res && (
        <>
          <div className="num grid grid-cols-2 gap-3 p-4 text-xs md:grid-cols-5" data-testid="walk-forward-combined">
            {[["OOS trades", c.trades], ["Win rate", `${num(c.win_rate)}%`], ["Profit factor", pf(c.profit_factor)], ["Net P&L", rupee(c.net_pnl)], ["Max drawdown", rupee(c.max_drawdown)]].map(([k, v]) => (
              <div key={k}><div className="eyebrow !text-[10px]">{k}</div><div className={`text-lg font-semibold ${k === "Net P&L" ? pnlClass(c.net_pnl) : k === "Profit factor" && c.profit_factor < 1 ? "down" : ""}`}>{v}</div></div>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="dense num w-full" data-testid="table-walk-forward-folds">
              <thead><tr><th>Test period</th><th>Rules picked</th><th>Train trades</th><th>Train PF</th><th>Test trades</th><th>Test PF</th><th>Test net</th></tr></thead>
              <tbody>
                {res.folds.map((f, i) => (
                  <tr key={f.test} data-testid={`wf-fold-row-${i}`}>
                    <td>{f.test}</td>
                    <td className="!text-left text-slate-300">OR {f.params.or_minutes}m · RSI {f.params.rsi_long} · ADX≥{f.params.adx_min || 0} · {f.params.reward_risk}R · {f.params.entry_mode}</td>
                    <td>{f.train_metrics.trades}</td><td>{pf(f.train_metrics.profit_factor)}</td><td>{f.test_metrics.trades}</td>
                    <td className={f.test_metrics.profit_factor < 1 ? "down" : "up"}>{pf(f.test_metrics.profit_factor)}</td>
                    <td className={pnlClass(f.test_metrics.net_pnl)}>{rupee(f.test_metrics.net_pnl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="h-[200px] p-3" data-testid="chart-walk-forward-equity">
            <ResponsiveContainer>
              <LineChart data={res.equity}>
                <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={axis} minTickGap={50} padding={{ right: 24 }} />
                <YAxis domain={["auto", "auto"]} tick={axis} width={70} />
                <Tooltip contentStyle={{ background: "#121826", border: "1px solid #334155", fontSize: 11 }} />
                <Line dataKey="equity" stroke="#818CF8" dot={false} strokeWidth={2} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="px-4 pb-3 text-[11px] text-slate-500">{res.combos} rule combinations searched per fold. Only the test periods count, so this is the closest estimate of how re-optimising every 6 months would have performed.</p>
        </>
      )}
    </div>
  );
};

const KIND = STRATEGY_LABELS;

export const StrategyLab = ({ disabled }) => {
  const [pricing, setPricing] = useState("estimated");
  const { res, job, run, busy } = useResearch("strategies", (r) => {
    const best = [...r.strategies].sort((a, b) => (b.out_of_sample.profit_factor || 0) - (a.out_of_sample.profit_factor || 0))[0];
    toast.success(`Strategy comparison done: best OOS PF ${pf(best.out_of_sample.profit_factor)} (${best.name})`);
  });
  useEffect(() => { if (res?.pricing) setPricing(res.pricing); }, [res?.pricing]);
  return (
    <div className="panel" data-testid="panel-strategy-lab">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-800 p-4">
        <span className="eyebrow">Different strategies · side-by-side comparison</span>
        <PricingToggle value={pricing} onChange={setPricing} />
        <Button data-testid="button-run-strategies" disabled={disabled || busy} onClick={() => run("/research/strategies", { years: 3, oos_pct: 30, pricing })} className="ml-auto h-8 bg-indigo-500 text-xs text-white hover:bg-indigo-400">{job ? "Running…" : "Compare strategies"}</Button>
        <Progress job={job} id="strategies-progress" />
      </div>
      {res && <LabResults res={res} rows={res.strategies} id="strategies" />}
      {res && Object.keys(res.walk_forward || {}).length > 0 && (
        <div className="overflow-x-auto border-t border-slate-800">
          <div className="eyebrow px-4 pt-3">Walk-forward per strategy (12m train → 6m test, re-picked each fold)</div>
          <table className="dense num w-full" data-testid="table-strategies-walk-forward">
            <thead><tr><th>Strategy</th><th>Combos</th><th>OOS trades</th><th>Win %</th><th>PF</th><th>Net</th><th>Max DD</th><th>Fold PFs</th></tr></thead>
            <tbody>
              {Object.entries(res.walk_forward).map(([k, w]) => (
                <tr key={k} data-testid={`wf-row-${k}`}>
                  <td className="text-slate-300">{KIND[k] || k}</td><td>{w.combos}</td><td>{w.combined.trades}</td><td>{num(w.combined.win_rate)}</td>
                  <td className={w.combined.profit_factor >= 1.5 ? "up font-semibold" : w.combined.profit_factor < 1 ? "down" : ""}>{pf(w.combined.profit_factor)}</td>
                  <td className={pnlClass(w.combined.net_pnl)}>{rupee(w.combined.net_pnl)}</td><td>{rupee(w.combined.max_drawdown)}</td>
                  <td className="text-slate-400">{w.folds.map((f) => pf(f.test_metrics.profit_factor)).join(" · ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {res?.pricing === "recorded" && <p className="px-4 py-2 text-[11px] text-amber-400">Recorded pricing covers only the snapshot days; walk-forward is skipped until there's enough history. Trailing stops aren't applied with recorded prices.</p>}
    </div>
  );
};
